// "What your room needs" — docs/plans/2026-09-16-gear-requirements-brief.md
// §2, §3, §4. Pure: reads the document and the catalogue, reuses the same
// context/channel-derivation/assignment machinery as checkBuild() (never a
// second truth about who is driving what), and answers three questions:
//   - roomRequirements: what does this room need, regardless of what's placed?
//   - chainFit: does the PLACED receiver/processor + amps satisfy that?
//   - productFit: would ONE candidate product (browsed, not yet placed) satisfy it?

import type { EditorDoc, SpeakerEntry } from '@roomtreatment/core';
import type { Product } from '@roomtreatment/av-catalog';
import type { BuildContext } from './context';
import { buildContext } from './context';
import { isActiveProduct } from './assignment';
import { isSubwoofer, isOverhead } from './channels';
import { derivedLayoutString } from './rules/avrDecodeProcessing';
import { productName, fmtWatts, fmtOhms } from './copy';

export type HeadroomBand = 'too-weak' | 'just-enough' | 'comfortable' | 'more-than-needed';

export interface RoomRequirements {
  layout: string; // e.g. "5.1.2"
  passiveSpeakers: number; // each needs an amp channel
  activeSpeakers: number; // built-in amps, need no amp channel
  subwoofers: number; // need a sub output, not an amp channel
  needsHeightDecoding: boolean; // any overhead speaker present
  minLoadOhm: number | null; // lowest nominal impedance among passive speakers
  peakDb: number;
  power: {
    wattsPerChannel: number; // worst case
    speakerName: string; // who sets it
    distanceM: number;
    sensitivityDb: number;
  } | null; // null: no listener, or no passive speaker has sensitivity
  unknownSensitivity: string[]; // product names missing sensitivity
  noListener: boolean;
}

export interface ChainFit { // the user's chosen receiver/processor + amps, evaluated together
  hasFrontEnd: boolean; // a receiver or processor is placed
  frontEnd: { name: string; kind: 'receiver' | 'processor' } | null;
  channels: { have: number; need: number }; // amplified channels: receiver amps + power-amp channels
  power: {
    ratedW: number;
    channelsDriven: number | 'all' | null;
    ratio: number;
    band: HeadroomBand;
  } | null; // the weakest amplifier actually driving passive speakers
  load: 'ok' | 'set-4-ohm' | 'too-low' | 'unknown';
  /** The lowest impedance the chain is rated to drive: the highest rating
   *  among the amplifiers driving passive speakers (the one that limits
   *  it), or null when none publishes one. */
  ratedLoadOhm: number | null;
  subOutputs: { have: number; need: number } | null;
  heights: 'ok' | 'no-decoding' | 'not-needed';
  fits: boolean;
  fixes: string[]; // exact copy, see §4
}

const DEFAULT_PEAK_DB = 95;

function resolveProduct(speaker: SpeakerEntry, catalog: ReadonlyMap<string, Product>): Product | undefined {
  return speaker.productId ? catalog.get(speaker.productId) : undefined;
}

/** §3 "Headroom band". */
export function headroomBand(ratio: number): HeadroomBand {
  if (ratio < 1) return 'too-weak';
  if (ratio <= 1.3) return 'just-enough';
  if (ratio <= 3) return 'comfortable';
  return 'more-than-needed';
}

/** §3 "Required watts for one passive speaker at the seat": the published
 *  sensitivity, adjusted for the 2.83 V into 4 Ω convention (2.83 V into 4 Ω
 *  is 2 W, i.e. +3 dB over 1 W) — an `unstated` ref or any other impedance is
 *  used as published, and `inRoom` figures are used as published too (the
 *  room lifts the speaker equally). This is a deliberately different, and
 *  much simpler, model than PWR-02's headroom-warning SPL formula
 *  (rules/headroom.ts): that rule asks "does this specific amp already in
 *  the room clip at reference level"; this asks "how many watts does the
 *  room need at the user's chosen peak", a question `roomChecklist` never
 *  needed to answer before this brief.
 *
 *  Exported (gear-matching SEO architecture doc §4.1/§4.5 Phase 0) so a
 *  content page can show the same "how many watts" number `roomRequirements`
 *  already computes for the live editor, instead of a second formula. */
export function oneWattSensitivity(sensitivityDb: { v: number; ref: '2.83V' | '1W' | 'unstated' }, impedanceOhm: number): number {
  if (sensitivityDb.ref === '2.83V' && impedanceOhm === 4) return sensitivityDb.v - 3;
  return sensitivityDb.v;
}

/** §3 watts formula, rounded up to a whole watt. Exported alongside
 *  `oneWattSensitivity` — see its doc comment. */
export function wattsNeeded(peakDb: number, sens1W: number, distanceM: number): number {
  const raw = Math.pow(10, (peakDb - sens1W + 20 * Math.log10(distanceM)) / 10);
  return Math.ceil(raw);
}

// ── wattageTable ─────────────────────────────────────────────────────────

/** One row of a wattage table: the watts this speaker needs at `distanceM`,
 *  for each requested peak level. */
export interface WattageRow {
  distanceM: number;
  /** Same order as the `peakDbs` requested; `{peakDb, watts}` pairs so a
   *  caller never has to zip two parallel arrays back together. */
  atPeakDb: { peakDb: number; watts: number }[];
}

/**
 * The "how many watts do I need" table (gear-matching SEO architecture doc
 * §1.4 point 3 / §4.1): the exact `oneWattSensitivity`/`wattsNeeded` formula
 * `roomRequirements` already uses for a placed room, evaluated instead at a
 * small set of assumed seat distances and peak levels — no live room needed.
 * `null` when the speaker doesn't publish a nominal impedance or sensitivity
 * (the maker never published it — never guessed, per schema.ts's own rule),
 * so a caller can render "not enough data" rather than inventing a number.
 * Never used to decide a pass/fail verdict: `speakerAmpPairing`
 * (web/src/lib/gear/pairings.ts) deliberately zeroes `power` before calling
 * `productFit`, and this table does not change that.
 */
export function wattageTable(
  speaker: Pick<Product, 'specs'>,
  opts: { distancesM: readonly number[]; peakDbs: readonly number[] },
): WattageRow[] | null {
  if (speaker.specs.kind !== 'speaker') return null;
  const { sensitivityDb, impedanceOhm } = speaker.specs;
  if (!sensitivityDb || !impedanceOhm) return null;
  const sens1W = oneWattSensitivity(sensitivityDb, impedanceOhm.v);
  return opts.distancesM.map((distanceM) => ({
    distanceM,
    atPeakDb: opts.peakDbs.map((peakDb) => ({ peakDb, watts: wattsNeeded(peakDb, sens1W, distanceM) })),
  }));
}

export function roomRequirements(doc: EditorDoc, catalog: ReadonlyMap<string, Product>): RoomRequirements {
  const ctx = buildContext(doc, catalog, { hasRoomResults: true });
  return roomRequirementsFromContext(doc, ctx);
}

/** Same computation as `roomRequirements`, for callers that already built a
 *  `BuildContext` for this doc/catalog (`roomChecklist.ts`) — avoids paying
 *  for `buildContext`/`deriveChannels` a second time. Not part of the public
 *  contract; `index.ts` re-exports only `roomRequirements`. */
export function roomRequirementsFromContext(doc: EditorDoc, ctx: BuildContext): RoomRequirements {
  const peakDb = doc.listening?.peakDb ?? DEFAULT_PEAK_DB;

  // Counts (passive/active/sub/heights) come straight from `doc.speakers` —
  // subkind, product active flag, product overhead type — independent of
  // channel derivation, which needs a listener (bed-slot azimuth matching)
  // and would otherwise report 0 of everything before a seat is placed even
  // though speakers are sitting right there. Only `power` (distance to the
  // seat) genuinely needs one.
  let passiveSpeakers = 0;
  let activeSpeakers = 0;
  let subwoofers = 0;
  let needsHeightDecoding = false;
  let minLoadOhm: number | null = null;
  const unknownSensitivityNames = new Set<string>();

  interface Candidate { product: Product; watts: number; distanceM: number; sensDb: number }
  const candidates: Candidate[] = [];

  for (const entry of doc.speakers) {
    const product = resolveProduct(entry, ctx.catalog);

    if (isSubwoofer(entry, product)) { subwoofers += 1; continue; }
    if (isOverhead(entry, product)) needsHeightDecoding = true;

    if (isActiveProduct(product)) { activeSpeakers += 1; continue; }
    passiveSpeakers += 1;

    // Generic, unbound speakers count as passive 8 Ω with no sensitivity —
    // README/brief §3: "count toward channels only", never `unknownSensitivity`.
    if (!product || product.specs.kind !== 'speaker') {
      minLoadOhm = minLoadOhm == null ? 8 : Math.min(minLoadOhm, 8);
      continue;
    }

    const impedanceOhm = product.specs.impedanceOhm?.v ?? 8;
    minLoadOhm = minLoadOhm == null ? impedanceOhm : Math.min(minLoadOhm, impedanceOhm);

    const sensitivity = product.specs.sensitivityDb;
    if (!sensitivity) { unknownSensitivityNames.add(productName(product)); continue; }
    if (!ctx.listener) continue; // no seat yet — can't size the power for this speaker

    const sens1W = oneWattSensitivity(sensitivity, impedanceOhm);
    const [lx, ly, lz] = ctx.listener.position;
    const [px, py, pz] = entry.position;
    const distanceM = Math.hypot(px - lx, py - ly, pz - lz);
    candidates.push({ product, distanceM, sensDb: sens1W, watts: wattsNeeded(peakDb, sens1W, distanceM) });
  }

  let power: RoomRequirements['power'] = null;
  if (candidates.length > 0) {
    const worst = candidates.reduce((a, b) => (b.watts > a.watts ? b : a));
    power = {
      wattsPerChannel: worst.watts,
      speakerName: productName(worst.product),
      distanceM: worst.distanceM,
      sensitivityDb: worst.sensDb,
    };
  }

  return {
    // Unlike the counts above, the layout STRING stays channel-derived (bed/
    // overhead/sub counts) — README/AVR-02 precedent: this package always
    // prefers actually-derived channels over a stored layout enum, and this
    // display string, unlike the counts, has no correct answer before slots
    // can even be assigned.
    layout: derivedLayoutString(ctx.bedIds.length, ctx.subIds.length > 0, ctx.overheadIds.length),
    passiveSpeakers,
    activeSpeakers,
    subwoofers,
    needsHeightDecoding,
    minLoadOhm,
    peakDb,
    power,
    unknownSensitivity: [...unknownSensitivityNames].sort(),
    noListener: !ctx.listener,
  };
}

// ── chainFit ─────────────────────────────────────────────────────────────

interface DrivingAmpInfo {
  ratedW: number;
  channelsDriven: number;
  totalChannels: number;
  minImpedanceOhm: number | undefined;
  hasImpedanceSettings: boolean;
}

/** Every amp (internal or external) actually driving at least one passive
 *  speaker per `ctx.assignment` — README "AVR-03 and AVR-04" / "IMP-01" /
 *  "PWR-02" all read this same assignment; this just collects the amps
 *  instead of re-deriving who drives what. */
function drivingAmps(ctx: BuildContext): DrivingAmpInfo[] {
  if (!ctx.assignment) return [];
  const seen = new Set<string>();
  const result: DrivingAmpInfo[] = [];
  for (const a of ctx.assignment.bySpeaker.values()) {
    if (a.active || !a.driving || a.driving.kind === 'preout') continue;
    const key = a.driving.kind === 'internal' ? 'internal' : `ext:${a.driving.gearId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (a.driving.kind === 'internal') {
      const specs = ctx.avUnit?.specs;
      if (!specs?.power || specs.ampChannels.v === 0) continue;
      result.push({
        ratedW: specs.power.w8,
        channelsDriven: specs.power.channelsDriven,
        totalChannels: specs.ampChannels.v,
        minImpedanceOhm: specs.minImpedanceOhm?.v,
        hasImpedanceSettings: !!specs.impedanceSettings,
      });
    } else {
      const gearId = a.driving.gearId;
      const amp = ctx.amps.find((x) => x.gearId === gearId);
      if (!amp) continue;
      result.push({
        ratedW: amp.specs.power.w8,
        channelsDriven: amp.specs.power.channelsDriven,
        totalChannels: amp.specs.channels.v,
        minImpedanceOhm: amp.specs.minImpedanceOhm.v,
        hasImpedanceSettings: false, // no impedance-setting switch in the power-amp schema
      });
    }
  }
  return result;
}

interface FitFields {
  hasFrontEnd: boolean;
  frontEnd: ChainFit['frontEnd'];
  channels: ChainFit['channels'];
  power: ChainFit['power'];
  load: ChainFit['load'];
  ratedLoadOhm: ChainFit['ratedLoadOhm'];
  subOutputs: ChainFit['subOutputs'];
  heights: ChainFit['heights'];
}

/** The per-row fix line for each of the four §3/§4 concerns, kept separate
 *  from the flat `fixes` array so `roomChecklist.ts`'s three Amp rows can
 *  show the exact line that applies to THEM, rather than re-deriving it —
 *  the needs block (`fixes`) and the rows are built from the same strings. */
interface FitLines {
  channels?: string;
  power?: string;
  load?: string;
  heights?: string;
}

function computeFitFields(ctx: BuildContext, req: RoomRequirements): FitFields {
  const hasFrontEnd = !!ctx.avUnit;
  const frontEnd: ChainFit['frontEnd'] = ctx.avUnit
    ? { name: productName(ctx.avUnit.product), kind: ctx.avUnit.product.type === 'av_processor' ? 'processor' : 'receiver' }
    : null;

  const internalChannels = ctx.avUnit?.specs.ampChannels.v ?? 0;
  const externalChannels = ctx.amps.reduce((sum, a) => sum + a.specs.channels.v, 0);
  const channels = { have: hasFrontEnd ? internalChannels + externalChannels : 0, need: req.passiveSpeakers };

  const amps = drivingAmps(ctx);

  let power: ChainFit['power'] = null;
  if (req.power && amps.length > 0) {
    const weakest = amps.reduce((a, b) => (b.ratedW < a.ratedW ? b : a));
    const ratio = weakest.ratedW / req.power.wattsPerChannel;
    power = {
      ratedW: weakest.ratedW,
      channelsDriven: weakest.channelsDriven === weakest.totalChannels ? 'all' : weakest.channelsDriven,
      ratio,
      band: headroomBand(ratio),
    };
  }

  const rated = amps.map((a) => a.minImpedanceOhm).filter((z): z is number => z != null);
  const ratedLoadOhm = rated.length > 0 ? Math.max(...rated) : null;

  let load: ChainFit['load'] = 'ok';
  if (req.minLoadOhm != null) {
    const withImpedance = amps.filter((a): a is DrivingAmpInfo & { minImpedanceOhm: number } => a.minImpedanceOhm != null);
    if (withImpedance.length === 0) {
      load = 'unknown';
    } else {
      const worst = withImpedance.reduce((a, b) => (b.minImpedanceOhm > a.minImpedanceOhm ? b : a));
      if (req.minLoadOhm < 6 && worst.minImpedanceOhm <= 4) {
        load = worst.hasImpedanceSettings ? 'set-4-ohm' : 'ok';
      } else if (worst.minImpedanceOhm > req.minLoadOhm) {
        load = 'too-low';
      } else {
        load = 'ok';
      }
    }
  }

  let heights: ChainFit['heights'] = 'not-needed';
  if (req.needsHeightDecoding) {
    if (hasFrontEnd) {
      const { atmos, dtsx } = ctx.avUnit!.specs.heightDecoding.v;
      heights = atmos || dtsx ? 'ok' : 'no-decoding';
    } else {
      heights = 'no-decoding';
    }
  }

  let subOutputs: ChainFit['subOutputs'] = null;
  if (req.subwoofers > 0) {
    subOutputs = { have: hasFrontEnd ? ctx.avUnit!.specs.preouts.v.subOuts : 0, need: 1 };
  }

  return { hasFrontEnd, frontEnd, channels, power, load, ratedLoadOhm, subOutputs, heights };
}

function computeFitLines(ctx: BuildContext, req: RoomRequirements, fields: FitFields): FitLines {
  const { hasFrontEnd, frontEnd, channels, power, load, heights } = fields;
  const lines: FitLines = {};

  if (!hasFrontEnd) {
    if (channels.need > 0) lines.channels = `Add a receiver with at least ${channels.need} channels.`;
  } else if (frontEnd!.kind === 'processor' && ctx.amps.length === 0 && channels.need > 0) {
    lines.channels = `Add power amps for ${channels.need} channels.`;
  } else if (channels.have < channels.need) {
    const noPreoutBlocked = !!ctx.assignment && ctx.assignment.noPreout.size > 0;
    lines.channels = noPreoutBlocked
      ? `Pick a receiver with at least ${channels.need} channels.`
      : `Add a ${channels.need - channels.have}-channel power amp.`;
  }

  if (power) {
    if (power.band === 'too-weak') {
      lines.power = `Short by ${fmtWatts(req.power!.wattsPerChannel - power.ratedW)} W per channel. Pick a stronger receiver or add a power amp.`;
    } else if (power.band === 'more-than-needed') {
      lines.power = 'Plenty of power. A cheaper receiver would do.';
    }
  }

  if (load === 'set-4-ohm') {
    lines.load = 'Set the receiver to 4 Ω in its speaker setup.';
  } else if (load === 'too-low') {
    lines.load = `Your speakers dip to ${fmtOhms(req.minLoadOhm!)} Ω. This amp isn't rated for that.`;
  }

  if (heights === 'no-decoding' && hasFrontEnd) {
    lines.heights = "This receiver can't play overhead speakers. Pick one with Dolby Atmos.";
  }

  return lines;
}

function fitFrom(fields: FitFields, lines: FitLines): ChainFit {
  const { hasFrontEnd, frontEnd, channels, power, load, ratedLoadOhm, subOutputs, heights } = fields;
  const fits =
    channels.have >= channels.need
    && load !== 'too-low'
    && heights !== 'no-decoding'
    && (!power || power.band !== 'too-weak')
    && (!subOutputs || subOutputs.have >= subOutputs.need);
  const fixes = [lines.channels, lines.power, lines.load, lines.heights].filter((s): s is string => !!s);
  return { hasFrontEnd, frontEnd, channels, power, load, ratedLoadOhm, subOutputs, heights, fits, fixes };
}

export function chainFit(doc: EditorDoc, catalog: ReadonlyMap<string, Product>, req: RoomRequirements): ChainFit {
  const ctx = buildContext(doc, catalog, { hasRoomResults: true });
  const fields = computeFitFields(ctx, req);
  const lines = computeFitLines(ctx, req, fields);
  return fitFrom(fields, lines);
}

/** Same computation as `chainFit`, for `roomChecklist.ts` only: it already
 *  has a `BuildContext` for the same doc/catalog (needed for its other
 *  rows), and needs the per-concern fix lines broken out — see `FitLines` —
 *  so its three Amp rows show exactly the line the needs block shows, never
 *  a re-derived one. Not part of the public contract (`index.ts` re-exports
 *  only `chainFit`/`productFit`/`roomRequirements`). */
export function chainFitForChecklist(ctx: BuildContext, req: RoomRequirements): ChainFit & { fixLines: FitLines } {
  const fields = computeFitFields(ctx, req);
  const lines = computeFitLines(ctx, req, fields);
  return { ...fitFrom(fields, lines), fixLines: lines };
}

// ── productFit ───────────────────────────────────────────────────────────

export function productFit(product: Product, req: RoomRequirements): { fits: boolean; band: HeadroomBand | null; reason: string | null } {
  const specs = product.specs;

  let channelsHave: number;
  let minImpedanceOhm: number | undefined;
  let power: { w8: number } | undefined;

  if (specs.kind === 'av') {
    channelsHave = specs.ampChannels.v;
    minImpedanceOhm = specs.minImpedanceOhm?.v;
    power = specs.power;
    if (req.needsHeightDecoding && !(specs.heightDecoding.v.atmos || specs.heightDecoding.v.dtsx)) {
      return { fits: false, band: null, reason: 'No height decoding' };
    }
  } else if (specs.kind === 'power_amp') {
    channelsHave = specs.channels.v;
    minImpedanceOhm = specs.minImpedanceOhm.v;
    power = specs.power;
  } else {
    return { fits: false, band: null, reason: null };
  }

  if (channelsHave < req.passiveSpeakers) {
    return { fits: false, band: null, reason: 'Too few channels' };
  }
  if (req.minLoadOhm != null && minImpedanceOhm != null && minImpedanceOhm > req.minLoadOhm) {
    return { fits: false, band: null, reason: "Not rated for your speakers' impedance" };
  }
  if (req.power && power) {
    const ratio = power.w8 / req.power.wattsPerChannel;
    const band = headroomBand(ratio);
    if (band === 'too-weak') return { fits: false, band, reason: 'Too weak' };
    return { fits: true, band, reason: null };
  }
  return { fits: true, band: null, reason: null };
}
